import * as THREE from 'three';
import type { NestConfig } from '../sim/contracts';
import type { CameraRig } from './CameraRig';
import type { NestInterior } from '../render/NestInterior';

/**
 * Underground scene manager. Owns the descent / ascent transitions between
 * the surface world and a nest's interior chamber.
 *
 * Drives:
 *   - Player-ant world position (teleport on enter/exit)
 *   - Camera rig (fly-to + follow a point inside the chamber)
 *   - Which interior group is visible
 *   - Per-frame ascent trigger (exit ring click)
 */
export interface NestModeHooks {
  /** Get current player world position (so we can test vs nest radius). */
  getPlayerPos(): { x: number; y: number; z: number };
  /** Count of the player's surface followers — spawned inside the nest on descent. */
  countFollowers?: () => number;
  /** Move the player to a world XZ and reset their goal. */
  teleportPlayer(x: number, z: number): void;
  /** Restore normal above-ground camera behaviour. */
  restoreSurfaceCamera(): void;
  /** Surface-world roots to hide while underground + restore on ascent. */
  surfaceRoots: THREE.Object3D[];
  /** Called on descent (true) and ascent (false) to mute outdoor ambience. */
  setOutdoorAmbianceMuted?: (muted: boolean) => void;
  /** Fired when the player takes fatal damage inside an enemy nest. */
  onPlayerKilledUnderground?: () => void;
  /** Fired when the queen of `side` is killed inside her nest. */
  onQueenKilled?: (side: NestSide) => void;
  /** Which surface team the player belongs to — for enemy-nest detection. */
  playerTeam: 0 | 1;
}

export type NestSide = 'red' | 'black';

export class NestMode {
  active: NestSide | null = null;
  private readonly nestByTeam: Record<NestSide, { config: NestConfig; interior: NestInterior }>;
  /** The renderer scene — we stash the pre-descent background + environment
   *  and swap to solid black so the translucent green tubes pop. */
  scene: THREE.Scene | null = null;
  private prevBackground: THREE.Color | THREE.Texture | null = null;
  private prevEnvironment: THREE.Texture | null = null;
  private prevFog: THREE.Fog | THREE.FogExp2 | null = null;
  private prevMaxPolar = Math.PI * 0.495;
  private prevMinPolar = 0;
  private prevMinDistance = 1.2;
  private prevEnableRotate = true;
  /** When true, the player has a pending tunnel path to walk; cleared on arrival. */
  private hasGoal = false;
  playerUndergroundHp = 100;
  private hpBanner: HTMLDivElement | null = null;
  private attackingQueen = false;
  /** Player tunnel-path state. Player traverses the tunnel graph just like
   *  residents do — climbs down the curves instead of cutting through rock. */
  private playerPath: number[] = [];
  private playerPathIdx = 0;
  private playerT = 0;
  private playerCurrentNode = 0;
  private playerEdgeLength = 1;

  constructor(
    red: { config: NestConfig; interior: NestInterior },
    black: { config: NestConfig; interior: NestInterior },
    private camera: CameraRig,
    private hooks: NestModeHooks,
  ) {
    this.nestByTeam = { red, black };
  }

  /** Call each tick with the player position + dt. Auto-descends if the
   *  player is standing on a nest hole AND we're currently on the surface.
   *  Also integrates underground movement toward the current click goal. */
  update(dt: number): void {
    const pos = this.hooks.getPlayerPos();
    if (!this.active) {
      // Descent trigger: within 0.4 m of either nest centre on the surface.
      for (const side of ['red', 'black'] as NestSide[]) {
        const n = this.nestByTeam[side].config;
        const dx = pos.x - n.x;
        const dz = pos.z - n.z;
        if (dx * dx + dz * dz < 0.16) {
          this.descend(side);
          return;
        }
      }
      return;
    }

    // Underground: advance along the BFS-planned tunnel path so the player
    // climbs down the tube walls instead of teleporting through rock.
    const interior = this.nestByTeam[this.active].interior;
    if (this.hasGoal && this.playerPath.length > 0 && this.playerPathIdx > 0 && this.playerPathIdx < this.playerPath.length) {
      const SPEED = 3.2;  // m/s along the tube curve
      const from = this.playerPath[this.playerPathIdx - 1];
      const to = this.playerPath[this.playerPathIdx];
      const edge = interior.getEdgeCurve(from, to);
      this.playerT += (SPEED * dt) / Math.max(0.1, this.playerEdgeLength);
      if (this.playerT >= 1) {
        this.playerT = 0;
        this.playerCurrentNode = to;
        this.playerPathIdx++;
        if (this.playerPathIdx >= this.playerPath.length) {
          this.hasGoal = false;
          this.playerPath = [];
        } else {
          this.playerEdgeLength = interior.edgeLength(
            this.playerPath[this.playerPathIdx - 1],
            this.playerPath[this.playerPathIdx],
          );
        }
      }
      // Sample curve for world position.
      const scratch = new THREE.Vector3();
      if (edge) {
        const tEval = edge.forward ? this.playerT : (1 - this.playerT);
        edge.curve.getPointAt(Math.max(0, Math.min(1, tEval)), scratch);
        const origin = interior.group.position;
        this.hooks.teleportPlayer(scratch.x + origin.x, scratch.z + origin.z);
        // 3-D tangent for pitch, not just XZ yaw — the yellow ant actually
        // climbs up and down the tube walls instead of floating through them.
        const tAhead = edge.forward ? Math.min(1, tEval + 0.02) : Math.max(0, tEval - 0.02);
        const ahead = new THREE.Vector3();
        edge.curve.getPointAt(tAhead, ahead);
        const tangent = ahead.clone().sub(scratch);
        // Small lift so the body sits on the tube wall rather than sinking
        // through its centerline. Any larger offset can push the ant outside
        // the tube radius.
        interior.setPlayerMarkerLocal(scratch.x, scratch.y + 0.02, scratch.z, undefined, tangent);
      } else {
        // No curve found — just finish.
        this.hasGoal = false;
        this.playerPath = [];
      }
    }

    interior.setPlayerMarkerMoving(this.hasGoal);

    // Drive residents + check combat. `update` returns how much damage the
    // nest's guards landed on the player this tick.
    const origin = interior.group.position;
    const localX = pos.x - origin.x;
    const localZ = pos.z - origin.z;
    const localY = interior.playerMarker.position.y;
    const dmgIn = interior.update(dt, localX, localZ, localY);
    // Only snap Y to the nearest floor when NOT actively climbing a tube —
    // the curve walker already placed the marker at the right depth.
    if (!this.hasGoal) interior.setPlayerMarkerWorld(pos.x, pos.z);

    if (dmgIn > 0) {
      this.playerUndergroundHp -= dmgIn;
      if (this.playerUndergroundHp <= 0) {
        this.playerUndergroundHp = 0;
        this.hooks.onPlayerKilledUnderground?.();
        this.ascend();
        return;
      }
    }

    // Queen attack: while within 0.8 m of the queen, drain her HP.
    const queenLocal = interior.queen.clone().sub(origin);
    const qdx = queenLocal.x - localX;
    const qdz = queenLocal.z - localZ;
    const nearQueen = qdx * qdx + qdz * qdz < 0.64;
    if (nearQueen && this.attackingQueen) {
      if (interior.damageQueen(14 * dt)) {
        this.hooks.onQueenKilled?.(this.active);
        this.showBanner(`${this.active.toUpperCase()} QUEEN SLAIN`, 3000);
        this.ascend();
        return;
      }
    }
    // Return-fire: while attacking-queen, also hit nearby residents so guards
    // don't just chew on you while you're at the queen.
    if (this.attackingQueen) {
      interior.damageResidentsNear(localX, localZ, 0.4, 6, dt);
    }
    this.updateHpBanner(interior.queenHp);
  }

  /** Enter / leave "attacking the queen" state. Triggered by a click on the
   *  queen chamber floor while close to her. */
  private maybeStartQueenAttack(worldHit: THREE.Vector3): void {
    if (!this.active) return;
    const interior = this.nestByTeam[this.active].interior;
    const dx = worldHit.x - interior.queen.x;
    const dz = worldHit.z - interior.queen.z;
    if (dx * dx + dz * dz < 2 * 2) this.attackingQueen = true;
    else this.attackingQueen = false;
  }

  /** Click handler (raycaster hits inside an interior group). `worldPoint`
   *  is the raycast hit in world space. Also passes whether the hit was on
   *  the exit ring so we can ascend. */
  handleClick(worldPoint: THREE.Vector3, exitHit: boolean): void {
    if (!this.active) return;
    if (exitHit) {
      this.ascend();
      return;
    }
    const interior = this.nestByTeam[this.active].interior;
    const origin = interior.group.position;
    const localX = worldPoint.x - origin.x;
    const localY = worldPoint.y - origin.y;
    const localZ = worldPoint.z - origin.z;
    // Include Y so chambers stacked on the same XZ column (entry/hub/queen)
    // can each be targeted independently.
    const targetNode = interior.nearestNodeLocal(localX, localZ, localY);
    const path = interior.path(this.playerCurrentNode, targetNode);
    if (path.length > 1) {
      this.playerPath = path;
      this.playerPathIdx = 1;
      this.playerT = 0;
      this.playerEdgeLength = interior.edgeLength(path[0], path[1]);
      this.hasGoal = true;
    } else {
      // Same-node click — nothing to path.
      this.hasGoal = false;
      this.playerPath = [];
    }
    this.maybeStartQueenAttack(worldPoint);
  }

  private descend(side: NestSide): void {
    this.active = side;
    this.attackingQueen = false;
    this.playerUndergroundHp = 100;
    this.playerCurrentNode = 0;
    this.playerPath = [];
    this.playerPathIdx = 0;
    this.playerT = 0;
    const interior = this.nestByTeam[side].interior;
    const enemy = interior.team !== this.hooks.playerTeam;
    interior.setHostility(enemy);
    this.showBanner(enemy
      ? `INVADING ${side.toUpperCase()} NEST — kill the queen!`
      : `Inside your ${side.toUpperCase()} nest`, 2200);
    // Attach the interior's scene group so the renderer can see it. It was
    // detached on the last ascent to keep surface frames lean.
    if (this.scene && interior.group.parent !== this.scene) {
      this.scene.add(interior.group);
    }
    interior.show();
    // Bring the player's followers down with her. Counted on entry so we can
    // shadow the yellow ant's path through the tubes.
    const followerCount = this.hooks.countFollowers?.() ?? 0;
    if (followerCount > 0) void interior.spawnFollowers(followerCount);
    // Ant-farm mode: lock the camera to a straight-on side view of the XY
    // plane. No rotation, but pan and zoom are still allowed so the player can
    // scroll around and inspect chambers. Stash the surface constraints so
    // we can restore them on ascent.
    this.prevMaxPolar = this.camera.controls.maxPolarAngle;
    this.prevMinPolar = this.camera.controls.minPolarAngle;
    this.prevMinDistance = this.camera.controls.minDistance;
    this.prevEnableRotate = this.camera.controls.enableRotate;
    this.camera.controls.maxPolarAngle = Math.PI / 2;
    this.camera.controls.minPolarAngle = Math.PI / 2;
    this.camera.controls.minDistance = 0.4;
    this.camera.controls.enableRotate = false;
    this.camera.setAllowBelowGround(true);
    this.hooks.setOutdoorAmbianceMuted?.(true);
    // Swap the scene to a pure-black void so the translucent green tubes read
    // without surface HDRI bleed-through.
    if (this.scene) {
      this.prevBackground = this.scene.background as THREE.Color | THREE.Texture | null;
      this.prevEnvironment = this.scene.environment;
      this.prevFog = this.scene.fog;
      // Ant-farm view: fill the whole viewport with warm soil colour so any
      // area not covered by nest geometry still reads as dirt.
      this.scene.background = new THREE.Color(0x6b4a2f);
      this.scene.environment = null;
      this.scene.fog = null;
    }
    // Hide the surface world so the chamber reads clearly.
    for (const r of this.hooks.surfaceRoots) {
      r.userData.__wasVisible = r.visible;
      r.visible = false;
    }
    // Teleport player into the entry-chamber spawn.
    const entryWorld = interior.entry.clone();
    this.hooks.teleportPlayer(entryWorld.x, entryWorld.z);
    // Fly to the interior's authored camera view so the whole inverted tree
    // reads at a glance.
    const v = interior.cameraView;
    this.camera.setFollow(null);
    this.camera.setAntCam(null);
    // A surface click typically kicks off a flyTo toward the nest hole; if we
    // don't abort it, its next-tick lerp will overwrite the ant-farm view we
    // just installed and the camera appears stuck on the surface.
    this.camera.cancelFly();
    this.camera.camera.position.copy(v.pos);
    this.camera.controls.target.copy(v.target);
    this.camera.controls.update();
  }

  private ascend(): void {
    if (!this.active) return;
    const side = this.active;
    const config = this.nestByTeam[side].config;
    const interior = this.nestByTeam[side].interior;
    interior.hide();
    interior.clearFollowers();
    // Detach the whole interior subtree from the scene so the renderer skips
    // every tube/chamber/resident mesh while we're above ground.
    if (this.scene && interior.group.parent === this.scene) {
      this.scene.remove(interior.group);
    }
    this.active = null;
    this.hasGoal = false;
    this.attackingQueen = false;
    this.hideHpBanner();
    // Restore surface sky/env/fog + orbit constraints exactly as we found them.
    if (this.scene) {
      this.scene.background = this.prevBackground;
      this.scene.environment = this.prevEnvironment;
      this.scene.fog = this.prevFog;
    }
    this.camera.controls.maxPolarAngle = this.prevMaxPolar;
    this.camera.controls.minPolarAngle = this.prevMinPolar;
    this.camera.controls.minDistance = this.prevMinDistance;
    this.camera.controls.enableRotate = this.prevEnableRotate;
    this.camera.setAllowBelowGround(false);
    this.hooks.setOutdoorAmbianceMuted?.(false);
    // Restore surface world.
    for (const r of this.hooks.surfaceRoots) {
      if ('__wasVisible' in r.userData) r.visible = r.userData.__wasVisible !== false;
      else r.visible = true;
    }
    // Teleport player to just outside the nest rim so they don't re-trigger
    // the descent on the very next frame.
    const outX = config.x + config.radius * 1.2;
    const outZ = config.z;
    this.hooks.teleportPlayer(outX, outZ);
    this.hooks.restoreSurfaceCamera();
    this.camera.flyTo(config.x, 1.0, config.z, 0.7, { x: 0.8, y: 0.7, z: 0.8 });
  }

  addSurfaceRoot(r: THREE.Object3D): void {
    this.hooks.surfaceRoots.push(r);
  }

  /** Public wrapper for the minimap's sky-strip tap. */
  ascendFromInput(): void { this.ascend(); }

  private showBanner(text: string, ms: number): void {
    if (this.hpBanner) this.hpBanner.remove();
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = [
      'position:fixed', 'top:70px', 'left:50%', 'transform:translateX(-50%)',
      'padding:10px 18px',
      'background:rgba(15,20,25,0.92)', 'color:#ffeb70',
      'border:1px solid rgba(255,235,112,0.35)', 'border-radius:10px',
      'font:600 14px/1.3 system-ui, sans-serif', 'letter-spacing:0.08em',
      'z-index:120', 'box-shadow:0 4px 16px rgba(0,0,0,0.6)',
    ].join(';');
    document.body.appendChild(el);
    this.hpBanner = el;
    setTimeout(() => { if (this.hpBanner === el) { el.remove(); this.hpBanner = null; } }, ms);
  }

  private hpStatus: HTMLDivElement | null = null;
  private updateHpBanner(queenHp: number): void {
    if (!this.hpStatus) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed', 'top:8px', 'left:50%', 'transform:translateX(-50%)',
        'padding:6px 12px',
        'background:rgba(15,20,25,0.82)', 'color:#eee',
        'border:1px solid rgba(255,255,255,0.12)', 'border-radius:8px',
        'font:600 12px/1.2 system-ui, sans-serif',
        'z-index:7',
      ].join(';');
      document.body.appendChild(el);
      this.hpStatus = el;
    }
    this.hpStatus.style.display = 'block';
    this.hpStatus.textContent = `YOU ${Math.ceil(this.playerUndergroundHp)} HP   ·   QUEEN ${Math.ceil(queenHp)} HP`;
  }
  private hideHpBanner(): void {
    if (this.hpStatus) this.hpStatus.style.display = 'none';
  }

  get isUnderground(): boolean { return this.active !== null; }
  get walkables(): THREE.Mesh[] {
    if (!this.active) return [];
    return this.nestByTeam[this.active].interior.walkable;
  }
  get exitRingMarker(): { x: number; y: number; z: number } | null {
    if (!this.active) return null;
    return this.nestByTeam[this.active].interior.localToWorld(
      this.nestByTeam[this.active].interior.exitMarker.clone(),
    );
  }
}
